import { IAttachmentMetadata, IAttachmentWithData, IAuditNode } from '@alberta/konexi-shared';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ModalController, Platform, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subject, from, interval } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Logger } from 'src/app/common/logging/logger';
import { InfoBoxType } from 'src/app/shared/components/common/info-box/info-box.component';
import { LoadingModalComponent } from 'src/app/shared/components/common/loading-modal/loading-modal-page';
import { SwodocImage } from 'src/app/shared/models/swodoc/swodoc-image';
import { AuthService } from 'src/app/shared/services/auth.service';
import { SignoPadService } from 'src/app/shared/services/signopad/signopad.service';
import { SignoPadSignature } from 'src/app/shared/services/signopad/signopad.types';
import { v4 } from 'uuid';

import { ATTACHMENT_LOCAL_PREFIX } from '@services/attachment/attachment-service.interface';
import { DocumentEditService } from '../../document-edit.service';
import { SwodocNodeSignatureModal } from '../signature/modal/swodoc-node-signature.modal';

@Component({
  selector: 'swodoc-node-signature-signotec',
  templateUrl: 'swodoc-node-signature-signotec.html',
  styleUrls: ['swodoc-node-signature-signotec.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SwodocNodeSignatureSignotec implements OnInit, OnDestroy {
  static TAG = 'SwodocNodeSignatureSignotec';
  @Input() node: IAuditNode;
  @Input() documentEditService: DocumentEditService;
  @Output() changed = new EventEmitter<IAuditNode>();
  @Output() imageAdded = new EventEmitter<SwodocImage>();

  public warningType = InfoBoxType.Information;
  public signatureImage: Partial<IAttachmentWithData>;
  public signingCert: string;
  public readyForSignature = true;
  public isCordova: boolean;
  private _waitingForSignature = false;
  private _ngUnsubscribe = new Subject();
  private _loadingModal: HTMLIonModalElement;
  private _userEmail: string;

  constructor(
    private _modalCtrl: ModalController,
    private _platform: Platform,
    private _signoPadService: SignoPadService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _authService: AuthService,
    private _toastController: ToastController,
    private _translateService: TranslateService,
    private _logger: Logger
  ) {
    this.isCordova = this._platform.is('hybrid');
  }

  async ngOnInit() {
    this._userEmail = await this._authService.getUserEmail();
    this.initComponent();
    if (!this.isCordova) {
      this._setupSignopad();
    }
  }

  private async _setupSignopad() {
    await this._signoPadService.init();
    this._signoPadService
      .observeSignatures()
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(this._onSignature.bind(this));

    this._signoPadService
      .observeReadyForSignature()
      .pipe(takeUntil(this._ngUnsubscribe), distinctUntilChanged())
      .subscribe(this._onReadyForSignature.bind(this));

    this._signoPadService
      .observeSignatureCanceled()
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(this._onSignatureCanceled.bind(this));

    interval(1000)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(this._signoPadService.readyCheck.bind(this._signoPadService));

    // UNCOMMENT FOR DEBUGGING WITHOUT SIGNOPAD DEVICE:
    // this._signoPadService.demoMode();
  }

  public initComponent() {
    const images = this.documentEditService
      .getImagesForNodeId(this.node.id)
      .sort((a: any, b: any) =>
        a?.metadata?.createdAt && b?.metadata?.createdAt
          ? new Date(a.metadata.createdAt).valueOf() - new Date(b.metadata.createdAt).valueOf()
          : 1
      );
    this.signatureImage = images[images.length - 1];
    this._changeDetectorRef.markForCheck();
  }

  async signWithSignopad() {
    this._waitingForSignature = true;
    this._logger.addBreadcrumb({
      message: `${SwodocNodeSignatureSignotec.TAG} signWithSignopad`,
      type: 'debug',
    });
    this._signoPadService.startSignature(
      this._translateService.instant('audit.node.signature.signDocument'),
      this._userEmail || this._translateService.instant('audit.node.signature.signature')
    );

    this._loadingModal = await this._modalCtrl.create({
      cssClass: 'loading-modal',
      component: LoadingModalComponent,
      showBackdrop: true,
      backdropDismiss: false,
      componentProps: {
        header: this._translateService.instant('audit.node.signature.signAndSubmit'),
        message: from(Promise.resolve(this._translateService.instant('audit.node.signature.waitingForSignature'))),
      },
    });

    from(this._loadingModal.onDidDismiss())
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(async _ => {
        await this._signoPadService.cancelSignature();
        this._logger.addBreadcrumb({
          message: `${SwodocNodeSignatureSignotec.TAG} signWithSignopad, canceled via loading dialog`,
          type: 'debug',
        });
      });
    await this._loadingModal.present();
  }

  async signWithBrowserSignaturePad() {
    const signatureModal = await this._modalCtrl.create({
      component: SwodocNodeSignatureModal,
      backdropDismiss: false,
      cssClass: 'fullPageModal',
    });

    await signatureModal.present();
    this._logger.addBreadcrumb({
      message: `${SwodocNodeSignatureSignotec.TAG} signInBrowser`,
      type: 'debug',
    });
    const result = await signatureModal.onDidDismiss();
    const modalData = result?.data;
    if (!modalData || !modalData.signature) {
      return;
    }
    this._saveSignature(modalData.signature);
    this._changeDetectorRef.markForCheck();
  }

  private _onSignature(signature: SignoPadSignature) {
    if (this._waitingForSignature) {
      this._waitingForSignature = false;
      this._logger.addBreadcrumb({
        message: `${SwodocNodeSignatureSignotec.TAG} _onSignature`,
        type: 'debug',
        data: {
          signatureSize: signature?.image?.length,
          certSize: signature?.cert?.length,
        },
      });
      this.signingCert = signature.cert;
      this._loadingModal?.dismiss();
      this._saveSignature(signature.image, signature.cert);
    }
  }

  private _saveSignature(signatureImageB64: string, cert?: string) {
    const signatureId = `${ATTACHMENT_LOCAL_PREFIX}${v4()}`;
    const metadata: Partial<IAttachmentMetadata> = {
      auditNodeId: this.node.id,
      type: 102, // SwodocSignature
    };
    if (cert != null) {
      metadata.signoPadPublicKey = cert;
    }
    const newImage = {
      _id: signatureId,
      metadata: metadata,
      data: signatureImageB64,
    } as Partial<IAttachmentWithData>;
    if (this.signatureImage) {
      this.documentEditService.removeImage(this.signatureImage._id);
    }

    this.signatureImage = newImage;
    this.documentEditService.addImage(newImage);
    this.changed.emit(this.node);

    this._logger.addBreadcrumb({
      message: `${SwodocNodeSignatureSignotec.TAG} _saveSignature changed.emit`,
      type: 'debug',
    });
    this._changeDetectorRef.markForCheck();
  }

  private async _onReadyForSignature(isReady: boolean) {
    this._logger.addBreadcrumb({
      message: `${SwodocNodeSignatureSignotec.TAG} _onReadyForSignature`,
      type: 'debug',
    });
    if (this.readyForSignature === false && isReady === true) {
      const toast = await this._toastController.create({
        message: this._translateService.instant('audit.node.signature.signoPadReady'),
        duration: 7500,
        position: 'bottom',
        cssClass: 'toast-success',
      });
      await toast.present();
    }
    if (isReady === false) {
      if (this._loadingModal != null) {
        this._loadingModal.dismiss();
      }
    }
    this.readyForSignature = isReady;
    this._changeDetectorRef.markForCheck();
  }

  private async _onSignatureCanceled(canceled: boolean) {
    if (canceled === true) {
      if (this._loadingModal != null) {
        this._loadingModal.dismiss();
      }
    }
    this._logger.addBreadcrumb({
      message: `${SwodocNodeSignatureSignotec.TAG} _onSignatureCanceled signature canceled via signopad`,
      type: 'debug',
    });
    this._changeDetectorRef.markForCheck();
  }

  async ngOnDestroy(): Promise<void> {
    try {
      await this._signoPadService.teardown();
    } finally {
      this._ngUnsubscribe.next(null);
      this._ngUnsubscribe.complete();
    }
  }
}
